// Sequence engine: everything the Edge needs to know about a motion example (a
// short run of completed candles), derived from its data alone. No drawing
// here; the chart, the player and the Read flow all read from this.
//
// Example format (QUSHi lesson data template):
//   { id, stage: "teach" | "callIt" | "retry", contextCandles?, candles: [OHLC],
//     freezeAfter? (Call It / Retry), answer, caption? (Teach), evidence: [...],
//     explanation (paragraphs), source?: { ticker, timeframe, start, provider } }
// Structure examples add their own question and options, and the swings:
//   question, options: [{ value, label }], swings: [{ candle, type: "high" | "low" }]
// Candle numbers count from 1, context candles included.

import { validateOhlc } from "../candle/candleModel";

export const STAGES = ["teach", "callIt", "retry"];

// How a timeframe is written on screen.
export const TIMEFRAME_LABEL = { "5m": "5 min", "15m": "15 min", "1h": "1 hour", "1d": "1 day" };

// Comparison kinds measured close to close; the rest compare inclusive ranges.
const CLOSE_KINDS = ["distance"];
const RANGE_KINDS = ["bodyExpansion", "bodyContraction", "closes"];

// Swing comparison kinds, with the swing type each compares and the name the
// later swing is given on the chart.
export const SWING_KINDS = {
  higherHigh: { type: "high", name: "Higher high" },
  higherLow: { type: "low", name: "Higher low" },
  lowerHigh: { type: "high", name: "Lower high" },
  lowerLow: { type: "low", name: "Lower low" },
};

// The last candle the learner sees: the freeze for Call It / Retry, the whole
// sequence for Teach.
export function lastShown(ex) {
  return ex.stage === "teach" ? ex.candles.length : ex.freezeAfter;
}

export const contextCount = (ex) => ex.contextCandles || 0;

// Every problem in one example, as readable strings (empty when valid). The
// Read refuses to load rather than draw or grade something wrong.
//   answers  the answer ids this Read grades against
export function validateExample(ex, answers) {
  const errors = [];
  const n = Array.isArray(ex.candles) ? ex.candles.length : 0;
  if (!ex.id) errors.push("missing id");
  if (!STAGES.includes(ex.stage)) errors.push(`unknown stage "${ex.stage}"`);
  if (!n) errors.push("no candles");
  (ex.candles || []).forEach((c, i) => {
    const err = validateOhlc(c);
    if (err) errors.push(`candle ${i + 1}: ${err}`);
  });
  const ctx = contextCount(ex);
  if (!Number.isInteger(ctx) || ctx < 0 || ctx >= n) errors.push("contextCandles out of range");
  if (ex.stage !== "teach") {
    if (!Number.isInteger(ex.freezeAfter) || ex.freezeAfter <= ctx || ex.freezeAfter > n) {
      errors.push("freezeAfter missing or out of range");
    }
  }
  if (!answers.includes(ex.answer)) errors.push(`unknown answer "${ex.answer}"`);

  const last = lastShown(ex) || n;
  const inRange = (k) => Number.isInteger(k) && k >= 1 && k <= last;
  const swingType = (k) => (ex.swings || []).find((s) => s.candle === k)?.type;
  (ex.swings || []).forEach((s, i) => {
    if (!inRange(s.candle)) errors.push(`swing ${i + 1}: candle out of range`);
    if (!["high", "low"].includes(s.type)) errors.push(`swing ${i + 1}: type must be high or low`);
  });
  (ex.evidence || []).forEach((e, i) => {
    const at = `evidence ${i + 1}`;
    if (e.type === "candle") {
      if (!inRange(e.candle)) errors.push(`${at}: candle out of range`);
      if (e.kind === "extension") {
        if (e.candle < 2) errors.push(`${at}: extension needs a previous candle`);
        if (!["high", "low"].includes(e.boundary)) errors.push(`${at}: boundary must be high or low`);
      }
    } else if (e.type === "relation") {
      if (!inRange(e.from) || !inRange(e.to) || e.from > e.to) errors.push(`${at}: range out of order`);
    } else if (e.type === "comparison") {
      const close = CLOSE_KINDS.includes(e.kind);
      if (!close && !RANGE_KINDS.includes(e.kind)) errors.push(`${at}: unknown comparison kind "${e.kind}"`);
      ["earlier", "later"].forEach((side) => {
        const s = e[side] || {};
        const [a, b] = close ? [s.fromClose, s.toClose] : [s.from, s.to];
        if (!inRange(a) || !inRange(b) || a > b) errors.push(`${at}: ${side} stretch out of range`);
      });
    } else if (e.type === "swingComparison") {
      const kind = SWING_KINDS[e.kind];
      if (!kind) errors.push(`${at}: unknown swing comparison kind "${e.kind}"`);
      else if (swingType(e.from) !== kind.type || swingType(e.to) !== kind.type || e.from >= e.to) {
        errors.push(`${at}: from and to must be marked swing ${kind.type}s, in order`);
      }
    } else if (e.type === "swingBoundary") {
      if (swingType(e.swing) !== e.boundary) errors.push(`${at}: swing must be a marked swing ${e.boundary}`);
      if (!swingName(ex, e.swing)) errors.push(`${at}: no comparison names this swing`);
      if (!inRange(e.from) || !inRange(e.to) || e.from > e.to) errors.push(`${at}: range out of order`);
    } else {
      errors.push(`${at}: unknown type "${e.type}"`);
    }
  });

  if (ex.source) {
    const s = ex.source;
    if (!s.ticker) errors.push("source: missing ticker");
    if (!TIMEFRAME_LABEL[s.timeframe]) errors.push(`source: unknown timeframe "${s.timeframe}"`);
    if (Number.isNaN(Date.parse(s.start))) errors.push("source: start is not a valid time");
  }
  return errors;
}

// Candles highlighted by one evidence item (numbers from 1). A close-to-close
// stretch starts at fromClose but highlights the candles after it.
export function stretchCandles(e, side) {
  const s = e[side];
  if (CLOSE_KINDS.includes(e.kind)) return range(s.fromClose + 1, s.toClose);
  return range(s.from, s.to);
}

export function evidenceCandles(e) {
  if (e.type === "candle") return e.kind === "extension" ? [e.candle - 1, e.candle] : [e.candle];
  if (e.type === "relation") return range(e.from, e.to);
  if (e.type === "swingComparison") return [e.from, e.to];
  if (e.type === "swingBoundary") return [e.swing, ...range(e.from, e.to)];
  return [...stretchCandles(e, "earlier"), ...stretchCandles(e, "later")];
}

export const isCloseComparison = (e) => e.type === "comparison" && CLOSE_KINDS.includes(e.kind);

export const isSwingEvidence = (e) => e.type === "swingComparison" || e.type === "swingBoundary";

// The name a comparison gives a swing ("Higher low"), which the boundary line
// repeats (client). Null when no comparison ends at that swing.
export function swingName(ex, k) {
  const e = (ex.evidence || []).find((x) => x.type === "swingComparison" && x.to === k && SWING_KINDS[x.kind]);
  return e ? SWING_KINDS[e.kind].name : null;
}

function range(a, b) {
  const out = [];
  for (let i = a; i <= b; i += 1) out.push(i);
  return out;
}

// Visible price range for the candles shown, plus headroom.
export function sequenceDomain(candles, pad = 0.08) {
  const lo = Math.min(...candles.map((c) => c.low));
  const hi = Math.max(...candles.map((c) => c.high));
  const span = hi - lo || 1;
  return [lo - span * pad, hi + span * pad];
}

// "Sep 30, 2026": candle 1's opening time, shown in Eastern time.
export function sourceDate(start) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(start));
}

// "META · 15 min · Sep 30, 2026"
export function sourceLine(source) {
  return [source.ticker, TIMEFRAME_LABEL[source.timeframe], sourceDate(source.start)].join(" · ");
}

// Direction of the move a sequence shows: the last candle shown closes above
// or below the starting reference (the context candle's close, or candle 1).
export function moveDirection(ex) {
  const start = ex.candles[Math.max(contextCount(ex), 1) - 1].close;
  const end = ex.candles[lastShown(ex) - 1].close;
  return end >= start ? "up" : "down";
}

// Display only: prices keep their supplied precision in the data. Two
// decimals, half-up (client): toFixed alone can round a true .xx5 down
// (598.165 -> "598.16") because of binary float error, so the value is first
// scaled to cents with the float noise cleared, then rounded.
export function formatPrice(p) {
  const cents = Math.round(Math.abs(Number((p * 100).toFixed(6))));
  return `${p < 0 ? "-" : ""}${(cents / 100).toFixed(2)}`;
}
